import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import i18n from '~/i18n'
import Button from '~/Common-Objects/UI/Forms/Button/Button'
import Icons from '~/Common-Objects/UI/Icons/index'
import NewUserForm from '~/Common-Objects/NewUserForm/newUserForm.jsx'
import ProgressNavSmall from '~/Common-Objects/Navigation/ProgressNavSmall'
import StickyNavBottom from '~/Common-Objects/Navigation/StickNavBottom'
import TextArea from '~/Common-Objects/UI/Forms/TextArea/TextArea'
import TextInput from '~/Common-Objects/UI/Forms/TextInput/TextInput'
import Toggle from '~/Common-Objects/UI/Forms/Toggle/Toggle'
import UpsellChip from '~/Common-Objects/UI/Chips/Upsell.jsx'
import DisplayCard from '~/Common-Objects/UI/DisplayCard'
import axios from '~/data/http/axios'
import * as siteMasterActions from '~/data/actions/siteActions'
import { composeGenerateRequestBody } from '~/utils/aiTemplateBuilder'
import { persistAiGeneratedMarkup } from '~/utils/persistAiGeneratedMarkup'
import LookAndFeelPicker from './lookAndFeel'
import LightPreview from './lightPreview'
import ChoiceIllustration from './choiceArt'
import GenerationLoading from './generationLoading'
import './style.scss'

const STEPS = {
    choice: 'choice',
    existingLook: 'existingLook',
    existingContent: 'existingContent',
    interview: 'interview',
    generateLook: 'generateLook',
    generating: 'generating',
    preview: 'preview'
}

export default function AiCreateWizard({
    kind = 'email',
    history,
    campaign,
    saveCampaign,
    changeStep,
    landingName,
    onStartFromScratch,
    onPreviewActive,
    onCancel
}) {
    const dispatch = useDispatch()
    const accountMaster = useSelector((state) => state.accountMaster)
    const account = accountMaster?.accountMaster
    const savedProfile = account?.options?.aiCompanyProfile || {}

    const [step, setStep] = useState(STEPS.choice)
    const [layout, setLayout] = useState(null)
    const [aiPrompt, setAiPrompt] = useState('')
    const [pastedContent, setPastedContent] = useState('')
    const [companyAbout, setCompanyAbout] = useState(savedProfile.about || '')
    const [message, setMessage] = useState(savedProfile.message || '')
    const [websiteUrl, setWebsiteUrl] = useState(savedProfile.websiteUrl || account?.websiteAddress || '')
    const [persistCompanyProfile, setPersistCompanyProfile] = useState(false)
    const [useAccountBranding, setUseAccountBranding] = useState(false)
    const [creationPath, setCreationPath] = useState(null)
    const [error, setError] = useState('')
    const [result, setResult] = useState(null)

    const ns = 'templates:add.magicTools'
    const landingChoiceKeys = {
        subtitle: 'landingSubtitle',
        scratchTitle: 'landingScratchTitle',
        scratchBody: 'landingScratchBody',
        existingTitle: 'landingExistingTitle',
        existingBody: 'landingExistingBody',
        generateTitle: 'landingGenerateTitle',
        generateBody: 'landingGenerateBody'
    }
    const choiceText = (key) => i18n.t(`${ns}.${kind === 'landing' ? landingChoiceKeys[key] : key}`)
    const choiceHeading = (titleKey, withAiLabel) => (
        <h4 className='mar-b10'>
            <span>{choiceText(titleKey)}</span>
            {withAiLabel && (
                <UpsellChip icon='sparkle' className='ai-create-wizard__ai-label'>
                    {i18n.t(`${ns}.aiLabel`)}
                </UpsellChip>
            )}
        </h4>
    )

    useEffect(() => {
        if (typeof onPreviewActive === 'function') {
            onPreviewActive(step === STEPS.preview)
        }
        // Parent may pass an inline callback; only re-run when the step changes.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [step])

    const canContinueContent = pastedContent.trim().length > 0
    const canContinueInterview = companyAbout.trim() || message.trim()
    const showExistingSteps =
        creationPath === 'existing' &&
        [STEPS.existingLook, STEPS.existingContent, STEPS.generating, STEPS.preview].includes(step)
    const existingStep = step === STEPS.existingLook ? 1 : step === STEPS.preview ? 3 : 2
    const existingStepLabels = [
        i18n.t(`${ns}.chooseTemplateStep`),
        i18n.t(`${ns}.addContentStep`),
        i18n.t(`${ns}.reviewDetailsStep`)
    ]

    const buildGenerateBody = (activeLayout) =>
        composeGenerateRequestBody({
            kind,
            prompt: activeLayout?.type === 'ai' ? aiPrompt : undefined,
            pastedContent,
            companyAbout,
            message,
            websiteUrl,
            persistCompanyProfile,
            useAccountBranding,
            sourceThemeId: activeLayout?.type === 'theme' ? activeLayout.id : undefined,
            sourceTemplateId: activeLayout?.type === 'template' ? activeLayout.id : undefined,
            noStyle: activeLayout?.type === 'none' || activeLayout?.type === 'ai',
            templateName: campaign?.campaignName,
            campaignId: campaign?.id,
            landingName
        })

    const extractError = (err) => {
        if (err?.data?.error) return err.data.error
        if (err?.response?.data?.error) return err.response.data.error
        if (err?.message && err.message !== 'Network Error') return err.message
        return i18n.t(`${ns}.genericError`)
    }

    const runGenerate = async (selectedLayout) => {
        const activeLayout =
            selectedLayout && ['theme', 'template', 'none', 'ai'].includes(selectedLayout.type)
                ? selectedLayout
                : layout
        if (!activeLayout) return

        if (!account?.approved) {
            dispatch(
                siteMasterActions.alterFullpage(
                    <NewUserForm
                        from='campaign'
                        finish={() => dispatch(siteMasterActions.alterFullpage(undefined))}
                    />
                )
            )
            return
        }

        setLayout(activeLayout)
        const fromStep = step
        setError('')
        setStep(STEPS.generating)
        try {
            const endpoint = kind === 'landing' ? '/ai/landing/generate' : '/ai/template/generate'
            const response = await axios.post(endpoint, buildGenerateBody(activeLayout), { timeout: 120000 })
            const data = response.data || {}
            if (kind === 'landing' && !data.landingPageId) {
                throw new Error(i18n.t(`${ns}.genericError`))
            }
            if (kind === 'email' && !data.templateId) {
                throw new Error(i18n.t(`${ns}.genericError`))
            }
            const persisted = await persistAiGeneratedMarkup({
                kind,
                templateId: data.templateId,
                landingPageId: data.landingPageId
            })
            if (kind === 'email' && campaign && saveCampaign) {
                const template =
                    persisted.template || (data.templateId ? { id: data.templateId, templateType: 0 } : null)
                campaign.TemplateId = data.templateId
                if (template) {
                    campaign.Template = {
                        ...template,
                        id: template.id || data.templateId
                    }
                }
                await saveCampaign(campaign)
            }
            setResult({
                ...data,
                previewHtml: persisted.html,
                previewUrl: persisted.url
            })
            setStep(STEPS.preview)
        } catch (err) {
            setError(extractError(err))
            setStep(fromStep)
        }
    }

    const openAdvanced = () => {
        if (kind === 'landing') {
            history.push(`/cp/templates/add/dragDropv2/?landingPageId=${result.landingPageId}`)
            return
        }
        history.push(`/cp/templates/add/dragDropv2/${result.templateId}`)
    }

    const goToCampaignReview = () => {
        if (kind === 'email' && campaign && result?.templateId) {
            campaign.TemplateId = result.templateId
            if (!campaign.Template?.id) {
                campaign.Template = {
                    ...(campaign.Template || {}),
                    id: result.templateId,
                    templateType: campaign.Template?.templateType ?? 0
                }
            }
        }
        if (typeof changeStep === 'function') changeStep(3, true)
    }

    const goBack = () => {
        if (step === STEPS.choice && typeof onCancel === 'function') onCancel()
        else if (step === STEPS.existingLook || step === STEPS.interview) setStep(STEPS.choice)
        else if (step === STEPS.existingContent) setStep(STEPS.existingLook)
        else if (step === STEPS.generateLook) setStep(STEPS.interview)
        else if (step === STEPS.preview) setStep(STEPS.choice)
    }

    const stickyNav = (continueProps) => {
        if (step === STEPS.generating || step === STEPS.preview) return null
        if (kind === 'landing') {
            if (!continueProps) return null
            return (
                <div className='ai-create-wizard__review-actions mar-t20'>
                    <Button
                        className='button-primary'
                        iconRight={<Icons icon='arrowRight' />}
                        disabled={continueProps.disabled}
                        onClick={continueProps.onClick}
                        data-test={continueProps.test}
                    >
                        {continueProps.label}
                    </Button>
                </div>
            )
        }
        const showBack = step !== STEPS.choice || typeof onCancel === 'function'
        if (!showBack && !continueProps) return null
        return (
            <StickyNavBottom>
                <div
                    className='quickFlex'
                    style={{ justifyContent: 'space-between', alignItems: 'center', width: '100%' }}
                >
                    {showBack ? (
                        <Button
                            iconLeft={<Icons icon='arrowLeft' />}
                            onClick={goBack}
                            data-test='ai-create-back'
                        >
                            {i18n.t(`${ns}.back`)}
                        </Button>
                    ) : (
                        <div />
                    )}
                    {continueProps ? (
                        <Button
                            className='button-primary mar-l5'
                            iconRight={<Icons icon='arrowRight' />}
                            disabled={continueProps.disabled}
                            onClick={continueProps.onClick}
                            data-test={continueProps.test}
                        >
                            {continueProps.label}
                        </Button>
                    ) : null}
                </div>
            </StickyNavBottom>
        )
    }

    return (
        <div
            className={step === STEPS.preview ? 'ai-create-wizard ai-create-wizard--preview' : 'ai-create-wizard'}
            data-test='ai-create-wizard'
        >
            {showExistingSteps && <ProgressNavSmall steps={3} step={existingStep} labels={existingStepLabels} />}
            {step === STEPS.choice && (
                <>
                    <div className='ai-create-wizard__header'>
                        <h2 className='ai-create-wizard__title'>
                            {kind === 'landing' ? i18n.t('form:landingPages.add.header') : i18n.t(`${ns}.title`)}
                        </h2>
                        <p className='ai-create-wizard__subtitle'>{choiceText('subtitle')}</p>
                    </div>
                    <div className='ai-create-wizard__cards'>
                        <div data-test='ai-create-path-scratch'>
                            <DisplayCard className='ai-create-wizard__card' onClick={onStartFromScratch}>
                                <DisplayCard.Content>
                                    <div className='ai-create-wizard__card-art'>
                                        <ChoiceIllustration variant='scratch' />
                                    </div>
                                    {choiceHeading('scratchTitle', false)}
                                    <p>{choiceText('scratchBody')}</p>
                                </DisplayCard.Content>
                            </DisplayCard>
                        </div>
                        <div data-test='ai-create-path-existing'>
                            <DisplayCard
                                className='ai-create-wizard__card'
                                onClick={() => {
                                    setCreationPath('existing')
                                    setStep(STEPS.existingLook)
                                }}
                            >
                                <DisplayCard.Content>
                                    <div className='ai-create-wizard__card-art'>
                                        <ChoiceIllustration variant='existing' />
                                    </div>
                                    {choiceHeading('existingTitle', kind === 'landing')}
                                    <p>{choiceText('existingBody')}</p>
                                </DisplayCard.Content>
                            </DisplayCard>
                        </div>
                        <div data-test='ai-create-path-generate'>
                            <DisplayCard
                                className='ai-create-wizard__card'
                                onClick={() => {
                                    setCreationPath('interview')
                                    setStep(STEPS.interview)
                                }}
                            >
                                <DisplayCard.Content>
                                    <div className='ai-create-wizard__card-art'>
                                        <ChoiceIllustration variant='generate' />
                                    </div>
                                    {choiceHeading('generateTitle', kind === 'landing')}
                                    <p>{choiceText('generateBody')}</p>
                                </DisplayCard.Content>
                            </DisplayCard>
                        </div>
                    </div>
                    {stickyNav()}
                </>
            )}

            {step === STEPS.existingLook && (
                <>
                    <div data-test='ai-create-look-and-feel'>
                        <h3 className='mar-b10'>{i18n.t(`${ns}.lookAndFeelTitle`)}</h3>
                        <p className='mar-b20'>{i18n.t(`${ns}.lookAndFeelSubtitle`)}</p>
                        <LookAndFeelPicker
                            kind={kind}
                            selected={layout}
                            prompt={aiPrompt}
                            onPromptChange={setAiPrompt}
                            onUsePrompt={() => {
                                setLayout({ type: 'ai' })
                                setError('')
                                setStep(STEPS.existingContent)
                            }}
                            onSelect={(next) => {
                                setLayout(next)
                                setStep(STEPS.existingContent)
                            }}
                        />
                    </div>
                    {stickyNav()}
                </>
            )}

            {step === STEPS.existingContent && (
                <>
                    <h3 className='mar-b10'>{i18n.t(`${ns}.addContentTitle`)}</h3>
                    <p className='mar-b20'>{i18n.t(`${ns}.addContentSubtitle`)}</p>
                    <TextArea
                        name='pastedContent'
                        rows={10}
                        value={pastedContent}
                        onChange={(ev) => setPastedContent(ev.target.value)}
                        placeholder={i18n.t(`${ns}.addContentPlaceholder`)}
                    />
                    {/* <Toggle
                        name='useAccountBranding'
                        outerClassName='mar-t20 mar-b15'
                        checked={useAccountBranding}
                        onChange={() => setUseAccountBranding(!useAccountBranding)}
                        after={i18n.t(`${ns}.useAccountBranding`)}
                    /> */}
                    {error ? <div className='text-red mar-b15'>{error}</div> : null}
                    {stickyNav({
                        label: i18n.t(`${ns}.generateButton`),
                        disabled: !canContinueContent || !layout,
                        onClick: () => runGenerate(),
                        test: 'ai-create-generate'
                    })}
                </>
            )}

            {step === STEPS.interview && (
                <>
                    <h3 className='mar-b10'>{i18n.t(`${ns}.interviewTitle`)}</h3>
                    <p className='mar-b20'>{i18n.t(`${ns}.interviewSubtitle`)}</p>
                    <TextArea
                        name='companyAbout'
                        className='mar-b20'
                        rows={4}
                        label={i18n.t(`${ns}.companyLabel`)}
                        value={companyAbout}
                        onChange={(ev) => setCompanyAbout(ev.target.value)}
                    />
                    <TextArea
                        name='message'
                        className='mar-b20'
                        rows={4}
                        label={i18n.t(`${ns}.messageLabel`)}
                        value={message}
                        onChange={(ev) => setMessage(ev.target.value)}
                    />
                    <TextInput
                        name='websiteUrl'
                        className='mar-b20'
                        label={i18n.t(`${ns}.websiteLabel`)}
                        value={websiteUrl}
                        onChange={(ev) => setWebsiteUrl(ev.target.value)}
                    />
                    <Toggle
                        name='persistCompanyProfile'
                        outerClassName='mar-b20'
                        checked={persistCompanyProfile}
                        onChange={() => setPersistCompanyProfile(!persistCompanyProfile)}
                        after={i18n.t(`${ns}.saveForLater`)}
                    />
                    {error ? <div className='text-red mar-b15'>{error}</div> : null}
                    {stickyNav({
                        label: i18n.t(`${ns}.continue`),
                        disabled: !canContinueInterview,
                        onClick: () => setStep(STEPS.generateLook),
                        test: 'ai-create-interview-continue'
                    })}
                </>
            )}

            {step === STEPS.generateLook && (
                <>
                    <h3 className='mar-b10'>{i18n.t(`${ns}.suggestTitle`)}</h3>
                    <p className='mar-b20'>{i18n.t(`${ns}.suggestSubtitle`)}</p>
                    {error ? <div className='text-red mar-b15'>{error}</div> : null}
                    <LookAndFeelPicker kind={kind} showNoStyle={true} selected={layout} onSelect={runGenerate} />
                    {stickyNav()}
                </>
            )}

            {step === STEPS.generating && <GenerationLoading />}

            {step === STEPS.preview && result && (
                <LightPreview
                    kind={kind}
                    templateId={result.templateId}
                    landingPageId={result.landingPageId}
                    previewHtml={result.previewHtml}
                    title={showExistingSteps ? i18n.t(`${ns}.reviewDetailsTitle`) : undefined}
                    subtitle={showExistingSteps ? i18n.t(`${ns}.reviewDetailsSubtitle`) : undefined}
                    inlineActions={kind === 'landing'}
                    campaign={campaign}
                    changeStep={changeStep}
                    history={history}
                    onAdvancedEditor={openAdvanced}
                    onReviewDetails={goToCampaignReview}
                    onSendNow={goToCampaignReview}
                    onTryAnother={() => {
                        setResult(null)
                        setStep(creationPath === 'interview' ? STEPS.generateLook : STEPS.existingContent)
                    }}
                />
            )}
        </div>
    )
}
